import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Award, DollarSign, Users, Shield, Clock, ThumbsUp, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { TourCard } from '../components/TourCard';
import { domesticTours, internationalTours, honeymoonPackages, whyChooseUs, testimonials } from '../mockData';

export const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const iconMap = {
    Award,
    DollarSign,
    Users,
    Shield,
    Clock,
    ThumbsUp
  };

  const featuredTours = [
    domesticTours[0], // Golden Triangle
    internationalTours[1], // Singapore-Malaysia
    honeymoonPackages[0] // Maldives
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1507525428034-b723cf961d3e)',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-sky-900/80 to-emerald-900/80"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in">
            Explore India & the World with <span className="text-sky-300">Sky Trips</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Create unforgettable memories with our expertly curated tour packages
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/tours">
              <Button size="lg" className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Explore Tours
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="https://wa.me/919920995700" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-6 text-lg">
                <Phone className="mr-2 w-5 h-5" />
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Featured Tours Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Popular Tour Packages</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover our most loved destinations and experiences. From romantic getaways to family adventures.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            {featuredTours.map((tour) => (
              <TourCard key={tour.id} tour={tour} />
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/tours">
              <Button size="lg" className="bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white px-8">
                View All Tours
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Tour Categories Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Explore by Category</h2>
            <p className="text-gray-600">Find the perfect tour package for your travel style</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Domestic Tours', image: 'https://images.unsplash.com/photo-1564507592333-c60657eea523', link: '/tours/domestic', desc: 'Explore incredible India' },
              { title: 'International Tours', image: 'https://images.unsplash.com/photo-1528543606781-2f6e6857f318', link: '/tours/international', desc: 'Discover the world' },
              { title: 'Honeymoon Packages', image: 'https://images.unsplash.com/photo-1648538923547-074724ca7a18', link: '/tours/honeymoon', desc: 'Romantic escapes' },
              { title: 'Group Tours', image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e', link: '/tours/group', desc: 'Travel with friends' },
              { title: 'Pilgrim Tours', image: 'https://images.unsplash.com/photo-1548013146-72479768bada', link: '/tours/pilgrim', desc: 'Spiritual journeys' },
              { title: 'Corporate Outings', image: 'https://images.pexels.com/photos/951927/pexels-photo-951927.jpeg', link: '/tours/group', desc: 'Team building trips' }
            ].map((category, index) => (
              <Link key={index} to={category.link}>
                <div className="relative h-64 rounded-lg overflow-hidden group cursor-pointer">
                  <img
                    src={category.image}
                    alt={category.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-6">
                    <h3 className="text-2xl font-bold text-white mb-1">{category.title}</h3>
                    <p className="text-gray-200">{category.desc}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gradient-to-br from-sky-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Why Choose Sky Trips?</h2>
            <p className="text-gray-600">Your trusted travel partner for memorable journeys</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-emerald-500 rounded-full flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">What Our Travelers Say</h2>
            <p className="text-gray-600">Real experiences from real travelers</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-gray-50 p-6 rounded-lg shadow-md">
                <div className="flex mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-800">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.location}</p>
                  <p className="text-xs text-sky-600 mt-1">{testimonial.tour}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-sky-600 to-emerald-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Start Your Journey?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let us plan your perfect holiday. Contact us today for a free quote!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Get Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="https://wa.me/919920995700" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-6 text-lg">
                <Phone className="mr-2 w-5 h-5" />
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};